import { fetchCentreDashboard } from 'api/procurement'

export const rupee = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`

// Client-side mirror of services.usage.assert_month_overall_within_budget: the
// month's TOTAL across all four categories is a hard cap (a single category may
// overspend). Computes whether setting `category` to `projectedCategoryValue`
// would push the month's overall used past the overall budget.
//
// projectedCategoryValue is the about-to-be value of THIS category's used:
//   - PM / Consumables submit -> the order's total (it replaces current used)
//   - Petty Cash / R&M        -> current used + the batch being added
//
// Returns { exceeded, over, budget, projectedOverall }. On any fetch error it
// returns exceeded:false so the client never blocks on its own — the server
// still enforces the cap.
export const checkOverallBudget = async ({
  centre,
  month,
  category,
  projectedCategoryValue
}) => {
  try {
    const data = await fetchCentreDashboard({ centre, month })
    const cats = data?.categories || []
    const budget = cats.reduce((s, c) => s + Number(c.base || 0), 0)
    const otherUsed = cats
      .filter((c) => c.key !== category)
      .reduce((s, c) => s + Number(c.utilised || 0), 0)
    const projectedOverall = otherUsed + Number(projectedCategoryValue || 0)
    const over = projectedOverall - budget
    // budget must be set (>0) for the cap to apply; tiny epsilon for rounding.
    return { exceeded: budget > 0 && over > 0.009, over, budget, projectedOverall }
  } catch (e) {
    return { exceeded: false, over: 0, budget: 0, projectedOverall: 0 }
  }
}

// A clear, actionable message for the over-budget pop-up. `noun` = order/spend.
export const overBudgetMessage = ({ over, budget, projectedOverall }, monthLabel, noun = 'order') =>
  `The total budget for ${monthLabel} is ${rupee(budget)} across all categories. ` +
  `This ${noun} takes the month's total to ${rupee(projectedOverall)} — ${rupee(over)} over budget. ` +
  `HQ will not be able to fulfil a ${noun} that crosses the month's total budget. ` +
  `Please bring it down by ${rupee(over)} to submit. (You can still Save draft.)`

// Shown when a SOFT category (program material, consumables, R&M) crosses its
// own budget. Petty cash never reaches this -- it is a hard cap and blocks.
export const softOverspendNote = (categoryLabel) =>
  `This crosses the ${categoryLabel} budget for the month. That is allowed ` +
  `while your centre's total budget still holds — the dashboard will show it ` +
  `as covered by the centre's spare.`

// Shown when petty cash would cross its own budget. Mirrors the server's
// PettyCashCapExceeded so a centre reads the same thing either way.
export const pettyCashCapMessage = ({ budget, already, adding, monthLabel }) =>
  `Petty cash for ${monthLabel} is capped at ${rupee(budget)}. You have ` +
  `already recorded ${rupee(already)}, so this ${rupee(adding)} would go ` +
  `${rupee(Number(already) + Number(adding) - Number(budget))} over. Reduce ` +
  `the amount, or ask HQ to move budget into petty cash.`
